import { useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { CACHE_DURATION } from 'src/utils/constants';

import axios from 'src/lib/axios';

import { SEARCH_PAGE_SIZE_OPTIONS } from 'src/sections/sales/search/utils/constant';
import { processAndValidateSearchText } from 'src/sections/sales/search/utils/utils';

export interface SearchIntentParams {
  page: number;
  page_size?: number;
  search_param?: string;
  search_query?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  sort_field?: string;
  sort_order?: 'asc' | 'desc';
  campaign?: string[] | null;
}

export type SearchType = 'loan_id' | 'manager_name' | 'loan_advisor_name' | 'borrower_phone_number';

const searchIntents = async (params: SearchIntentParams) => {
  // Return an empty result when search inputs are missing.
  // prevents unnecessary API calls and avoids inconsistent API responses when input values are cleared.
  if (!params.search_param) {
    return { results: [], totalCount: 0 };
  }

  const { data } = await axios.get('/intents/search/sales', {
    params,
    paramsSerializer: {
      indexes: null, // This removes the brackets from array parameters
    },
  });

  return {
    results: data?.results || [],
    totalCount: data?.total_count ?? data?.results.length,
  };
};

export function useSearchIntent(paginationOptions: SearchIntentParams) {
  const queryClient = useQueryClient();
  const {
    page,
    page_size,
    search_param,
    search_query,
    start_date,
    end_date,
    sort_field,
    sort_order,
    campaign,
  } = paginationOptions;

  // Validate the search query to prevent invalid API calls. This is crucial because
  // search state can be restored from session storage, which may contain stale or invalid data.
  const { processedText, error: validationError } = processAndValidateSearchText(
    search_query || '',
    search_param
  );

  const memoizedPaginationOptions = useMemo(
    () => ({
      page,
      page_size,
      search_param,
      search_query: processedText, // Use the processed text for the query
      start_date,
      end_date,
      sort_field,
      sort_order,
      campaign,
    }),
    [
      page,
      page_size,
      search_param,
      processedText,
      start_date,
      end_date,
      sort_field,
      sort_order,
      campaign,
    ]
  );

  // Memoize nextPageParams
  const memoizedNextPageParams = useMemo(
    () => ({
      ...memoizedPaginationOptions,
      page: memoizedPaginationOptions.page + 1,
    }),
    [memoizedPaginationOptions]
  );

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['intentsSearched', memoizedPaginationOptions],
    queryFn: () => searchIntents(memoizedPaginationOptions),
    // Enable the query only if there is a search parameter and no validation error.
    enabled: !!memoizedPaginationOptions.search_param && !validationError,
    staleTime: CACHE_DURATION.SALES_SEARCH,
    gcTime: CACHE_DURATION.SALES_SEARCH,
    // only keep previous data if there is a validation error and the category hasn't changed
    placeholderData: (previousData) => {
      if (validationError) {
        // Find all successful queries in the cache for 'intentsSearched'
        const queries = queryClient.getQueryCache().findAll({
          queryKey: ['intentsSearched'],
          fetchStatus: 'idle',
        });

        // Find the most recent query that matches the current search_param
        const matchingQuery = queries
          .filter((query) => {
            const params = query.queryKey[1] as SearchIntentParams | undefined;
            return (
              params?.search_param === memoizedPaginationOptions.search_param &&
              params?.page === memoizedPaginationOptions.page &&
              query.state.data
            );
          })
          .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0];

        // If a specific query is found, use its data. Otherwise, fallback to empty state.
        const cachedData = matchingQuery?.state.data as
          | { results: any; totalCount: any }
          | undefined;
        return cachedData ?? { results: [], totalCount: 0 };
      }
      return undefined;
    },
  });

  // Handle prefetching if there are more pages
  useEffect(() => {
    if (data) {
      // Only prefetch if there are more pages available
      const currentPageSize = paginationOptions.page_size || SEARCH_PAGE_SIZE_OPTIONS[0];
      const hasNextPage = data.totalCount > paginationOptions.page * currentPageSize;

      if (hasNextPage) {
        queryClient.prefetchQuery({
          queryKey: ['intentsSearched', memoizedNextPageParams],
          queryFn: () => searchIntents(memoizedNextPageParams),
          staleTime: CACHE_DURATION.SALES_SEARCH,
          gcTime: CACHE_DURATION.SALES_SEARCH,
        });
      }
    }
  }, [data, paginationOptions, queryClient, memoizedNextPageParams]);

  return {
    data: data?.results ?? [],
    totalCount: data?.totalCount ?? 0,
    isFetching,
    isLoading,
  };
}
